import { Bag as BagIcon, Media } from '@syconium/little-miss-figgy'

import { useFeatureFlag } from '../../../../../../app/_providers/ExperimentationProvider.client'
import { useFavorites } from '../../../../../../app/_providers/FavoritesProvider.client'
import { useLocalization } from '../../../../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { IntlShippingAndTaxMsg } from '../../../../../../brunswick/components/IntlShippingAndTaxMsg'
import { ProductCategories } from '../../../../../../brunswick/constants'
import { RefsContainer } from '../../../../../../brunswick/containers/refs'
import { Fit } from '../../../../../../constants'
import { CompleteTheSet } from '../../CompleteTheSet/CompleteTheSet'
import { AddToBagContainer } from '../../contexts/AddToBagContainer'
import { ProductDetailsContainer } from '../../contexts/ProductDetailsContainer'
import { ProductHighlights } from '../../ProductHighlights'
import WhyWeLove from '../../WhyWeLove'
import { AddToBagButton } from '../AddToBagButton'
import { CollectionLinks } from '../CollectionLink'
import { ProductDetailsAccordionSection } from '../ProductDetailsAccordionSection'
import * as SharedStyles from '../styles'

import * as Styles from './styles'

type GarmentDetailsProps = {
  /** The selectable attributes that are displayed for the current product. */
  attributes: JSX.Element
}

/**
 * Renders the necessary product details for the current product.
 *
 * Responsible for displaying price, details, and informational sections for the current product.
 * Selectable attributes for the product are provided by the parent component via the `attributes` prop.
 */
export default function GarmentDetails({ attributes }: GarmentDetailsProps) {
  const {
    detailsSection: { freeShippingMessage, freeReturnsMessage },
    pages: { product: productPageStrings },
  } = useTranslation()

  const {
    featuredProduct,
    productGroup,
    selectedVariant,
    isProductOnWaitlist,
    isProductFinalSale,
    wwltImage,
    collectionLinks,
    promoText,
  } = ProductDetailsContainer.useContainer()

  const { swPromoEligible } = featuredProduct
  const { detailsSectionAddToBagSectionRef } = RefsContainer.useContainer()

  const { addToBagCta, onAddToBagClick, isAddToCartInFlight } = AddToBagContainer.useContainer()

  const { isUSRegion } = useLocalization()

  const isKit = productGroup.rawCategory === ProductCategories.KIT

  const { enabled: favoritesEnabled } = useFavorites()

  const addToBagCtaIconTestDecision = useFeatureFlag<
    'atb-cta-bag-icon-test-control' | 'atb-cta-bag-icon-test-variant'
  >({
    key: 'atb-cta-bag-icon-test',
    controlVariant: 'atb-cta-bag-icon-test-control',
    ifAccessedPriorToDecisionInitialization: 'lock-pageview-to-controlVariant',
    skip: isProductOnWaitlist,
  })

  const isIconVisible =
    addToBagCtaIconTestDecision === 'atb-cta-bag-icon-test-variant' &&
    !isAddToCartInFlight &&
    !isProductOnWaitlist

  return (
    <div>
      {/* The price & tags for the current product */}
      <ProductHighlights />
      {/* Promotional messaging for the current product */}
      {promoText && (
        <SharedStyles.PromoText data-testid='promo-text'>{promoText}</SharedStyles.PromoText>
      )}
      {/* Render the attribute selectors for the current product */}
      {attributes}
      {/* Add to bag button */}
      <SharedStyles.AddToBagButtonContainer ref={detailsSectionAddToBagSectionRef}>
        <AddToBagButton
          onClickAddToBag={onAddToBagClick}
          isProcessing={isAddToCartInFlight}
          isWaitlist={isProductOnWaitlist}
          cta={addToBagCta}
          icon={isIconVisible && <BagIcon height={26} width={24} />}
        />
        {favoritesEnabled &&
        (selectedVariant?.rawColor || featuredProduct?.colorInfo?.rawName) &&
        !isKit ? (
          <Styles.FavoritesContainer>
            <Styles.PDPFavoritesButton
              productGroupHandle={productGroup.handle}
              size={selectedVariant?.rawSize ?? undefined}
              color={selectedVariant?.rawColor ?? featuredProduct?.colorInfo?.rawName ?? ''}
              fit={selectedVariant?.rawFit ?? Fit.REGULAR}
              trackingCategory='Favorites (PDP)'
              toastImage={featuredProduct.images?.nodes[0]?.source}
            />
          </Styles.FavoritesContainer>
        ) : null}
      </SharedStyles.AddToBagButtonContainer>
      {/* Display shipping/tax information for the current locale */}
      <Styles.IntlMsgWrap>
        <IntlShippingAndTaxMsg />
      </Styles.IntlMsgWrap>
      {/* Promo exclusion messaging */}
      {!swPromoEligible && (
        <Styles.PromoExclusionMessage>
          {productPageStrings.notEligibleForDiscount}
        </Styles.PromoExclusionMessage>
      )}
      {/* Free shipping messaging for domestic customers */}
      {isUSRegion && (
        <SharedStyles.ProductDetailsCopy style={{ margin: '24px 0', textAlign: 'center' }}>
          <Styles.ProductDetailsTitle>
            {freeShippingMessage} {!isProductFinalSale && freeReturnsMessage}
          </Styles.ProductDetailsTitle>
        </SharedStyles.ProductDetailsCopy>
      )}

      {!isKit && (
        <>
          <Media lessThan='md'>
            <Styles.WhyWeLoveDivider />
          </Media>
          <CompleteTheSet
            productHandle={featuredProduct.handle}
            completeTheSetAutofill={!!featuredProduct?.completeTheSetAutofill}
          />
        </>
      )}

      {/* WWL mobile section */}
      <Media lessThan='md'>
        <>
          <Styles.WhyWeLoveDivider />
          <WhyWeLove
            imageSrc={wwltImage?.source}
            whyWeLoveText={featuredProduct.whyWeLove}
            whyWeLoveIcons={featuredProduct.iconList}
          />
        </>
      </Media>

      {/* WWL desktop section */}
      <Media greaterThanOrEqual='md'>
        <Styles.WhyWeLoveText>{featuredProduct.whyWeLove}</Styles.WhyWeLoveText>
      </Media>

      {/* Accordions for product specifications (i.e. Details & Fit, Fabric & Care)*/}
      <ProductDetailsAccordionSection
        careInstructions={featuredProduct.careInstructions}
        fitModelsDescriptions={featuredProduct.fitModelsDescriptions}
        productGroupRtfFeatures={productGroup.rtfFeatures}
        productRtfFeatures={featuredProduct.rtfFeatures}
        rawMaterials={featuredProduct.rawMaterials}
        upc={selectedVariant?.upc}
      />
      {/* Links to collections related to the current product tags/selected variant color */}
      <CollectionLinks links={collectionLinks} />
    </div>
  )
}
